import {
  joinBlocks, spliceBlocks, setBlockProps, takeInlineTail, insertChars, insertBlockAfter, moveBlock,
} from './ops'
import type { CoreState } from './state'
import { indentItem } from './lists'
import { blockClass, gridSlot, isSelectableBlock, nodeSelection } from './block-selection'
import type { Block } from '@w/content-blocks/src/block'
import {
  children, clean, lastOf, leaf, plain, pointAt, remove, type Element, type Row,
} from './tree'

/** Row `row` leaves `wrapper` one level, in place: a list item's row with the item's other blocks ends the item and
 * splits its list (ProseMirror liftListItem); another container's row splits the container. The caret goes to its start. */
export function lift(state: CoreState, row: Row, wrapper: Element): void {
  const parent = row.parents[row.parents.indexOf(wrapper) - 1]
  const siblings = parent ? parent.children : state.blocks
  const index = siblings.indexOf(wrapper)
  if (wrapper.type === 'li') {
    const list = parent
    const listParent = row.parents[row.parents.indexOf(list) - 1]
    const outer = listParent ? listParent.children : state.blocks
    const at = outer.indexOf(list)
    // The item's other blocks (nested lists, atoms, paragraphs) leave with its row, in order (ProseMirror liftListItem).
    const owned = wrapper.children.filter((child) => child !== row.block)
    if (index === 0) {
      // A first item leaves before its list, which keeps its identity and its other items.
      spliceBlocks(state, list.children, 0, 1)
      spliceBlocks(state, outer, at, 0, row.block, ...owned)
    } else {
      const after = spliceBlocks(state, list.children, index + 1, list.children.length - index - 1)
      spliceBlocks(state, list.children, index, 1)
      spliceBlocks(state, outer, at + 1, 0, row.block, ...owned,
        ...(after.length > 0 ? [{ ...list, id: '', children: after } as Element] : []))
    }
  } else {
    const at = wrapper.children.indexOf(row.block)
    const after = spliceBlocks(state, wrapper.children, at + 1, wrapper.children.length - at - 1)
    spliceBlocks(state, wrapper.children, at, 1)
    spliceBlocks(state, siblings, index + 1, 0, row.block,
      ...(after.length > 0 ? [{ ...wrapper, id: '', children: after } as Element] : []))
  }
  clean(state, state.blocks)
  const point = pointAt(row, 0)
  state.selection = { anchor: point, focus: point }
}

export function split(state: CoreState, row: Row, offset: number, collapsed = false): void {
  let position = 0
  for (const child of row.block.children) {
    const end = position + plain(child).length
    if ((child.type === 'cloze' || child.type === 'question') &&
      offset > position && offset < end) {
      const point = pointAt(row, end)
      state.selection = { anchor: point, focus: point }
      return
    }
    position = end
  }
  const wrapper = row.parents.at(-1)
  if (row.block.type === 'pre') {
    const text = plain(row.block)
    const lineStart = text.lastIndexOf('\n', offset - 1) + 1
    const indent = text.slice(lineStart, offset).match(/^[\t ]*/)?.[0] ?? ''
    const inserted = '\n' + indent
    if (!insertChars(state, row.block.children, offset, inserted, 0)) {
      const tail = takeInlineTail(state, row.block.children, offset)
      spliceBlocks(state, row.block.children, row.block.children.length, 0,
        leaf(inserted), ...tail)
    }
    const point = pointAt(row, offset + 1 + indent.length)
    state.selection = { anchor: point, focus: point }
    return
  }
  // List item rules act on the item's own text, its first row; its other rows split like any row of a container.
  const item = wrapper?.type === 'li' && wrapper.children[0] === row.block
  // ProseMirror liftEmptyBlock: an empty row leaves its container (a list item outdents).
  if (!plain(row.block) && wrapper && (item || blockClass(wrapper) === 'container')) {
    const list = row.parents.at(-2)
    // An empty nested list item with a following sibling becomes text in its parent item, in place (superseded 2026-10-30
    // by Notion browser check, docs/notion-ux/decisions.md D7: AFTER `"" | text | fc22 | parent cbcb`, `c | f067 | parent
    // cbcb`); without one it outdents. Toggle lists keep the outdent (not observed).
    const inPlace = item && list?.type === 'list' && list.subtype !== 'toggle' &&
      list.children.indexOf(wrapper) < list.children.length - 1 && wrapper.children.length === 1
    if (item && !inPlace && row.parents.filter((parent) => parent.type === 'list')
      .length > 1) indentItem(state, row, true)
    else lift(state, row, wrapper)
    return
  }
  const length = plain(row.block).length
  // A quote is one block in Notion: Enter inside or at the end of its last text row leaves it, the text after the caret
  // becoming a text block (never a heading) after the quote (Notion browser checks imp04 "quo|ted" -> `[quote "quo"]`,
  // `[text "ted", parent: none]`; imp09 Enter at the end -> `quote:"abc"`, `text:"" sel 2@0`, both depth 0). At a row
  // start the generic rule below keeps an empty row above inside the quote (seed/enter-at-start/quote-paragraph; Notion
  // unchecked, queue C34). Earlier rows still split inside it.
  if (wrapper?.type === 'quote' && wrapper.children.at(-1) === row.block && ['p', 'h'].includes(row.block.type) && offset > 0) {
    const parent = row.parents.at(-2)
    const home = parent ? children(parent) : state.blocks
    const next: Element = { id: '', type: 'p', children: offset === length ? [] : takeInlineTail(state, row.block.children, offset) }
    insertBlockAfter(state, home, wrapper, next)
    const point = pointAt({ ...row, block: next }, 0)
    state.selection = { anchor: point, focus: point }
    return
  }
  if (offset === 0 && length > 0 && ['p', 'h'].includes(row.block.type)) {
    const empty: Element = { id: '', type: 'p', children: [] }
    if (item) {
      const list = lastOf(row.parents.slice(0, -1))
      spliceBlocks(state, list.children, list.children.indexOf(wrapper), 0,
        { ...wrapper, id: '', children: [empty] })
    } else spliceBlocks(state, row.siblings, row.siblings.indexOf(row.block), 0, empty)
    const point = pointAt(row, 0)
    state.selection = { anchor: point, focus: point }
    return
  }
  // INV-3: a split at the row end carries nothing, not even zero-width inlines (ProseMirror splitBlock atEnd).
  const next = { ...row.block, id: '',
    children: offset === length ? [] : takeInlineTail(state, row.block.children, offset) } as Element
  // The text after a heading split is a text block, at the end or inside (Notion browser check imp04: Enter in H1
  // "ab|cd" -> `[header "ab"], [text "cd"]`).
  if (row.block.type === 'h' && (offset > 0 || offset === length)) {
    setBlockProps(state, next, { type: 'p', level: undefined })
  } else if (row.block.type === 'h' && offset === 0) {
    setBlockProps(state, row.block, { type: 'p', level: undefined })
  }
  if (item) {
    const list = lastOf(row.parents.slice(0, -1))
    // A tail stays next to what follows it: a nested list right after the row takes the new item first (Notion);
    // otherwise the new item takes the item's later blocks after the tail (ProseMirror splitListItem). A split at the
    // row end carries no text, so the later blocks keep their item and the new empty item follows it.
    const nested = wrapper.children[1]
    // Enter at the end of a closed toggle's text header adds an empty toggle after it; the hidden body stays (Notion
    // browser check notion-browser-queue.md 40, imp04: `new 9c4819 notion-toggle-block, parent:none, after t`, `c
    // parent:19ae5b`).
    if (collapsed && list.type === 'list' && list.subtype === 'toggle' && row.block.type === 'p' && offset === length) {
      const sibling: Element = { id: '', type: 'li', children: [next] }
      insertBlockAfter(state, list.children, wrapper, sibling)
      const point = pointAt({ ...row, block: next }, 0)
      state.selection = { anchor: point, focus: point }
      return
    }
    // imp11: a closed toggle heading keeps its hidden body; Enter adds plain text at the same outer level.
    if (collapsed && list.type === 'list' && list.subtype === 'toggle' && row.block.type === 'h' && offset === length) {
      const parent = row.parents[row.parents.indexOf(list) - 1]
      const siblings = parent ? parent.children : state.blocks
      const after = spliceBlocks(state, list.children, list.children.indexOf(wrapper) + 1, list.children.length)
      spliceBlocks(state, siblings, siblings.indexOf(list) + 1, 0, next,
        ...(after.length > 0 ? [{ ...list, id: '', children: after } as Element] : []))
      const point = pointAt({ ...row, block: next }, 0)
      state.selection = { anchor: point, focus: point }
      return
    }
    // An open toggle, heading or text, creates its first body block even without an existing body (Notion queue 196,
    // imp11; queue 41, imp05; queue 120, imp26). A toggle whose body is a list keeps the next-item rule (not observed).
    if (list.type === 'list' && list.subtype === 'toggle' && offset === length &&
      (row.block.type === 'h' || (row.block.type === 'p' && nested?.type !== 'list'))) {
      spliceBlocks(state, wrapper.children, 1, 0, next)
      const point = pointAt({ ...row, block: next }, 0)
      state.selection = { anchor: point, focus: point }
      return
    }
    // An item whose only other block is its nested list splits into a sibling item that takes that list, at the end
    // (an empty sibling) or inside the text (the suffix) (superseded 2026-10-30 by Notion browser check, docs/notion-ux/
    // decisions.md D7: AFTER `"" | d572 | root`, `b | 7da1 | parent d572`). The list moves whole, so a peer's item in it
    // stays. Toggle lists keep the nested-first rule (not observed).
    const handsOver = nested?.type === 'list' && wrapper.children.length === 2 && list.type === 'list' &&
      list.subtype !== 'toggle'
    const later = handsOver || nested?.type === 'list' || offset === length ? []
      : spliceBlocks(state, wrapper.children, 1, wrapper.children.length - 1)
    const item = { ...wrapper, id: '', children: [next, ...later] }
    if ('done' in item) item.done = false
    if (handsOver) {
      insertBlockAfter(state, list.children, wrapper, item)
      moveBlock(state, nested, item.children, item.children.length)
    } else if (nested?.type === 'list') spliceBlocks(state, nested.children, 0, 0, item)
    else insertBlockAfter(state, list.children, wrapper, item)
  } else insertBlockAfter(state, row.siblings, row.block, next)
  const point = pointAt({ ...row, block: next }, 0)
  state.selection = { anchor: point, focus: point }
}

export function joinBoundary(
  state: CoreState, entries: Row[], position: { row: number; offset: number }, backward: boolean,
) {
  const row = entries[position.row]
  const wrapper = row.parents.at(-1)
  const boundary = backward ? position.offset === 0 : position.offset === plain(row.block).length
  if (!boundary) return false
  // Backspace at a code block's start, empty or not, leaves it as it is (Notion browser check imp25).
  if (backward && row.block.type === 'pre') return true
  if (backward && row.block.type === 'h' && (position.row > 0 || !plain(row.block))) {
    const previous = entries[position.row - 1]
    // An empty heading goes, the caret ending the row above (Notion browser check imp05: `header ""` -> block gone).
    if (!plain(row.block) && previous?.siblings === row.siblings) {
      remove(state, row)
      const point = pointAt(previous, plain(previous.block).length)
      state.selection = { anchor: point, focus: point }
      return true
    }
    // The empty text block before takes the heading's text and the heading goes (superseded 2026-10-30 by Notion browser
    // check: before `31060a text "" / 98d69c header "abc"` → after `31060a text "abc"`; it kept the heading before).
    if (previous?.block.type === 'p' && !plain(previous.block) &&
      previous.siblings === row.siblings) {
      spliceBlocks(state, previous.block.children, 0, previous.block.children.length,
        ...spliceBlocks(state, row.block.children, 0, row.block.children.length))
      remove(state, row)
      const point = pointAt(previous, 0)
      state.selection = { anchor: point, focus: point }
      return true
    }
    setBlockProps(state, row.block, { type: 'p', level: undefined })
    return true
  }
  const next = row.siblings[row.siblings.indexOf(row.block) + 1] as Block | undefined
  // An empty top-level row before an atom goes, leaving the block cursor before the atom.
  if (backward && !wrapper && !plain(row.block) && next && isSelectableBlock(next)) {
    remove(state, row)
    const point = { blockId: next.id, offset: 0 }
    state.selection = { anchor: point, focus: point }
    return true
  }
  if (backward && position.row === 0 && row.block.type === 'p' &&
    row.block.children.every((child) => (child.type === 'text' || child.type === 'chars') &&
      !/\S/u.test(plain(child))) && !wrapper && entries[1]) {
    remove(state, row)
    const point = pointAt(entries[1], 0)
    state.selection = { anchor: point, focus: point }
    return true
  }
  // Item rules act on the item's own row, its first; a later row joins or deletes like any row of its container.
  if (backward && wrapper?.type === 'li' && wrapper.children[0] === row.block) {
    lift(state, row, wrapper)
    return true
  }
  // Notion queue 242: forward Delete at a quote end skips a following divider and joins the next text row.
  const containerSiblings = row.parents.at(-2)?.children ?? state.blocks
  const afterWrapper = wrapper && containerSiblings[containerSiblings.indexOf(wrapper) + 1]
  if (!backward && wrapper?.type === 'quote' && wrapper.children.at(-1) === row.block &&
    afterWrapper?.type === 'hr') {
    const following = entries[position.row + 1]
    if (following?.block.type === 'p' && gridSlot(following) === gridSlot(row)) {
      const point = pointAt(row, position.offset)
      spliceBlocks(state, row.block.children, row.block.children.length, 0,
        ...spliceBlocks(state, following.block.children, 0, following.block.children.length))
      remove(state, following)
      state.selection = { anchor: point, focus: point }
    }
    return true
  }
  const adjacent = row.siblings[row.siblings.indexOf(row.block) + (backward ? -1 : 1)]
  // Lexical RangeSelection.deleteCharacter beside a block decorator: text keeps its place and the decorator goes; an
  // empty paragraph goes instead and the decorator becomes the node selection, for the next key to remove.
  // Notion Delete at a text end before the page's last block, a divider, does nothing (verified 2026-10-30 for exactly
  // that context, rule nux/selection/forward-delete-before-atom-selects-it); other decorators keep the Lexical rule below.
  if (adjacent?.type === 'hr' && !backward && plain(row.block) && row.siblings === state.blocks &&
    state.blocks.at(-1) === adjacent) return true
  if (adjacent && isSelectableBlock(adjacent)) {
    if (plain(row.block)) spliceBlocks(state, row.siblings, row.siblings.indexOf(adjacent), 1)
    else {
      remove(state, row)
      state.selection = nodeSelection(adjacent.id)
    }
    return true
  }
  const other = entries[position.row + (backward ? -1 : 1)]
  const following = row.siblings[row.siblings.indexOf(row.block) + 1]
  if (backward && other && row.block.type === 'p' && !plain(row.block) &&
    adjacent?.type === 'list' && following?.type === 'list' && adjacent.subtype === following.subtype) {
    const point = pointAt(other, plain(other.block).length)
    joinBlocks(state, adjacent, following)
    remove(state, row)
    clean(state, state.blocks)
    state.selection = { anchor: point, focus: point }
    return true
  }
  if (other && gridSlot(other) !== gridSlot(row)) {
    // Deletion never crosses a grid slot's edge (a table cell, a column: Lexical shadow roots); an empty paragraph
    // outside the grid goes and the caret enters it (Lexical deleteCharacter removes an empty anchor block beside a
    // shadow root and selects into it).
    if (!gridSlot(row) && row.block.type === 'p' && !plain(row.block)) {
      remove(state, row)
      const point = pointAt(other, backward ? plain(other.block).length : 0)
      state.selection = { anchor: point, focus: point }
    }
    return true
  }
  // Backspace at an empty text block's start after code removes it and the caret lands at the code end (Notion, verified
  // 2026-10-30, rule nux/selection/backspace-empty-after-code-selects-code); a nonempty one joins its text into the code
  // (Notion browser check imp23, rule nux/keys/backspace-after-code-no-merge), through the join below.
  if (backward && other && ['pre', 'code'].includes(other.block.type) && row.block.type === 'p' && !plain(row.block)) {
    remove(state, row)
    const point = pointAt(other, plain(other.block).length)
    state.selection = { anchor: point, focus: point }
    return true
  }
  const list = row.parents.findLast((parent) => parent.type === 'list')
  const otherList = other?.parents.findLast((parent) => parent.type === 'list')
  // Two sibling lists join at the boundary only from the joining list's own first row (item rules act on an item's first
  // row), never a list into one it holds.
  const opens = (entry: Row, owner: Element) => entry.parents.at(-1)?.type === 'li' &&
    entry.parents.at(-1)?.children[0] === entry.block && owner.children[0] === entry.parents.at(-1)
  if (list && otherList && other && list !== otherList && list.subtype === otherList.subtype &&
    !row.parents.includes(otherList) && !other.parents.includes(list) &&
    opens(backward ? row : other, backward ? list : otherList)) {
    const first = backward ? otherList : list
    const last = backward ? list : otherList
    joinBlocks(state, first, last)
    clean(state, state.blocks)
    return true
  }
  if (!backward && other && !plain(row.block)) {
    remove(state, row)
    const point = pointAt(other, 0)
    state.selection = { anchor: point, focus: point }
    clean(state, state.blocks)
    return true
  }
  // Forward Delete into a quote joins its text through the normal range deletion (Notion queue 112, imp25).
  // Other containers retain ProseMirror's lift-first-row behavior.
  const otherWrapper = other?.parents.at(-1)
  if (!backward && other && otherWrapper && blockClass(otherWrapper) === 'container' &&
    !row.parents.includes(otherWrapper) && otherWrapper.type !== 'quote') {
    const selection = state.selection
    lift(state, other, otherWrapper)
    state.selection = selection
    return true
  }
  if (backward && position.offset === 0 && wrapper &&
    (wrapper.type === 'li' || blockClass(wrapper) === 'container') && wrapper.children[0] === row.block &&
    (wrapper.type !== 'li' || row.parents.at(-2)?.children[0] === wrapper)) {
    lift(state, row, wrapper)
    return true
  }
  return false
}
